import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@frontegg/nextjs';

const CHANNEL = 'beamible.org-switch';
const SWITCH_START = 'switch:start';

/**
 * Cross-tab coordination for account switching. When one tab switches tenant it
 * clears the shared IndexedDB, which would otherwise make other tabs re-fetch the
 * old org with their still-old token and clobber the switch. On `switch:start`,
 * every other tab navigates to the minimal-provider switcher route so it stops
 * running `useLive` and drives its own switch to the new tenant.
 *
 * @param {Object} [options]
 * @param {Boolean} [options.listen] - subscribe and redirect on incoming messages
 * @returns {{ postStart: (tenantId: string) => void }}
 */
export default function useOrgSwitchChannel({ listen = false } = {}) {
  const { user } = useAuth();
  const router = useRouter();
  const channelRef = useRef(null);

  const getChannel = useCallback(() => {
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) {
      return null;
    }

    if (!channelRef.current) {
      channelRef.current = new BroadcastChannel(CHANNEL);
    }

    return channelRef.current;
  }, []);

  const postStart = useCallback(
    (tenantId) => {
      getChannel()?.postMessage({ type: SWITCH_START, tenantId });
    },
    [getChannel]
  );

  useEffect(() => {
    if (!listen) {
      return undefined;
    }

    const channel = getChannel();
    if (!channel) {
      return undefined;
    }

    const handleMessage = (event) => {
      const { type, tenantId } = event.data ?? {};

      // ignore anything but a switch to a tenant we're not already on
      if (type !== SWITCH_START || !tenantId || tenantId === user?.tenantId) {
        return;
      }

      // navigate to the minimal-provider switcher route, which unmounts useLive
      router.push(`/a/${tenantId}`);
    };

    channel.addEventListener('message', handleMessage);
    return () => channel.removeEventListener('message', handleMessage);
  }, [listen, getChannel, router, user?.tenantId]);

  useEffect(() => {
    return () => {
      channelRef.current?.close();
      channelRef.current = null;
    };
  }, []);

  return { postStart };
}
